<template>
  <v-dialog
    v-model="show"
    max-width="520"
    overlay-color="black"
    overlay-opacity="0.7"
    :dark="darkMode"
  >
    <v-card :dark="darkMode" class="claim-profile-card">
      <v-card-title class="headline" :class="titleClass">
        Complete your profile
      </v-card-title>
      <v-card-text :class="bodyClass">
        Add your name and set your password to finish your account setup.
      </v-card-text>
      <v-card-text>
        <v-text-field
          v-model="name"
          label="Full name"
          outlined
          dense
          :dark="darkMode"
          class="claim-profile-field"
        />
        <v-text-field
          v-model="password"
          label="Password"
          outlined
          dense
          :dark="darkMode"
          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          :type="showPassword ? 'text' : 'password'"
          class="claim-profile-field"
          @click:append="showPassword = !showPassword"
        />
        <v-text-field
          v-model="passwordConfirm"
          label="Confirm password"
          outlined
          dense
          :dark="darkMode"
          :append-icon="showPasswordConfirm ? 'mdi-eye' : 'mdi-eye-off'"
          :type="showPasswordConfirm ? 'text' : 'password'"
          class="claim-profile-field"
          @click:append="showPasswordConfirm = !showPasswordConfirm"
        />
        <v-alert
          v-if="error"
          type="error"
          dense
          class="mt-4 claim-profile-alert"
          :dark="darkMode"
        >
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="warning" :dark="darkMode" @click="dismiss">
          Later
        </v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!canSubmit"
          @click="submit"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ClaimProfileDialog",
  model: {
    prop: "value",
    event: "input"
  },
  props: {
    value: { type: Boolean, default: false }
  },
  data() {
    return {
      name: "",
      password: "",
      passwordConfirm: "",
      showPassword: false,
      showPasswordConfirm: false,
      saving: false,
      error: ""
    };
  },
  computed: {
    ...mapGetters(["darkMode"]),
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    titleClass() {
      return this.darkMode ? "white--text" : "";
    },
    bodyClass() {
      return this.darkMode ? "grey--text text--lighten-1" : "";
    },
    canSubmit() {
      return (
        this.name.trim().length > 0 &&
        this.password.length > 0 &&
        this.passwordConfirm.length > 0
      );
    }
  },
  watch: {
    value(next) {
      if (next) {
        const currentName = (this.$store.state.auth.user?.name || "").trim();
        this.name = currentName;
        this.password = "";
        this.passwordConfirm = "";
        this.error = "";
        this.showPassword = false;
        this.showPasswordConfirm = false;
      }
    }
  },
  methods: {
    dismiss() {
      this.show = false;
    },
    submit() {
      const name = this.name.trim();
      if (!name) {
        this.error = "Name is required";
        return;
      }
      if (!this.password) {
        this.error = "Password is required";
        return;
      }
      if (this.password !== this.passwordConfirm) {
        this.error = "Passwords do not match";
        return;
      }

      this.error = "";
      this.saving = true;
      this.$store
        .dispatch("auth/claimProfile", {
          name,
          password: this.password,
          password_confirm: this.passwordConfirm
        })
        .then(() => {
          this.$store.commit("raiseSnackbar", {
            text:
              "Profile completed. You can now log in with email and password.",
            color: "success",
            timeout: 5000
          });
          this.show = false;
        })
        .catch(error => {
          this.error =
            error?.body?.detail ||
            error?.data?.detail ||
            "Unable to update profile";
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>

<style scoped>
.claim-profile-card {
  border-radius: 12px;
}

.claim-profile-field ::v-deep .v-input__control .v-input__slot {
  border-radius: 10px;
}

.claim-profile-alert {
  border-radius: 10px;
}
</style>
